import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, X, MapPin, BedDouble, Bath, Maximize, ShieldCheck, Check, Minus } from "lucide-react";
import { api, fileUrl, formatINR } from "../lib/api";
import Seo from "../components/Seo";
import { useCompare, MAX_COMPARE } from "../context/CompareContext";

/** Attributes shown in the comparison matrix. Keep it 99acres-like — one column per property. */
const ROWS = [
    { key: "price", label: "Price", render: (p) => (p.price != null ? formatINR(p.price) : "—") },
    { key: "listing_type", label: "Listing", render: (p) => (p.listing_type || "—").toUpperCase() },
    { key: "property_type", label: "Property Type", render: (p) => cap(p.property_type) },
    { key: "subtype", label: "Subtype", render: (p) => p.subtype || "—" },
    { key: "city", label: "Location", render: (p) => [p.locality, p.city].filter(Boolean).join(", ") || "—" },
    { key: "bhk", label: "BHK", render: (p) => (p.bhk != null ? `${p.bhk} BHK` : "—") },
    { key: "bathrooms", label: "Bathrooms", render: (p) => p.bathrooms ?? "—" },
    { key: "area_sqft", label: "Area", render: (p) => (p.area_sqft ? `${p.area_sqft} sqft` : "—") },
    { key: "furnishing", label: "Furnishing", render: (p) => cap(p.furnishing) },
    { key: "floor_no", label: "Floor", render: (p) => (p.floor_no != null ? `${p.floor_no}${p.total_floors ? ` / ${p.total_floors}` : ""}` : "—") },
    { key: "age_years", label: "Age", render: (p) => (p.age_years != null ? `${p.age_years} yr${p.age_years === 1 ? "" : "s"}` : "—") },
    { key: "facing", label: "Facing", render: (p) => cap(p.facing) },
    { key: "parking", label: "Parking", render: (p) => (p.parking != null ? `${p.parking} spot${p.parking === 1 ? "" : "s"}` : "—") },
    { key: "possession_status", label: "Possession", render: (p) => cap(p.possession_status) },
    { key: "verified", label: "Verified", render: (p) => (p.verified ? <Check className="w-4 h-4 text-emerald-600 inline" /> : <Minus className="w-4 h-4 text-navy-500/40 inline" />) },
    { key: "featured", label: "Featured", render: (p) => (p.featured ? <Check className="w-4 h-4 text-gold-600 inline" /> : <Minus className="w-4 h-4 text-navy-500/40 inline" />) },
    { key: "amenities", label: "Amenities", render: (p) => (p.amenities?.length ? p.amenities.join(", ") : "—") },
];

function cap(v) {
    if (!v) return "—";
    return String(v).split("_").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
}

export default function Compare() {
    const { ids, remove, clear } = useCompare();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (ids.length === 0) {
                setItems([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const results = await Promise.all(
                    ids.map((id) => api.get(`/properties/${id}`).then((r) => r.data).catch(() => null))
                );
                if (cancelled) return;
                setItems(results.filter(Boolean));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [ids]);

    const empty = !loading && items.length === 0;

    // Which rows have at least one non-empty value across items? Hide fully-empty rows for a cleaner view.
    const visibleRows = useMemo(() => {
        if (items.length === 0) return ROWS;
        return ROWS.filter((row) => items.some((p) => {
            const v = p?.[row.key];
            if (Array.isArray(v)) return v.length > 0;
            return v !== null && v !== undefined && v !== "";
        }));
    }, [items]);

    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="Compare Properties"
                description="Compare up to 4 verified properties side by side on Global Property Mart — price, BHK, area, furnishing, amenities and more."
                path="/compare"
                keywords="compare properties, property comparison Nagpur, real estate compare India"
            />

            {/* Header */}
            <div className="bg-navy-950 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-10">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <Link to="/properties" className="text-xs text-gold-500 inline-flex items-center gap-1.5 mb-2 hover:text-gold-100">
                                <ArrowLeft className="w-3.5 h-3.5" /> Back to Properties
                            </Link>
                            <div className="label-overline mb-2 flex items-center gap-2">
                                <Scale className="w-3.5 h-3.5" /> Property Comparison
                            </div>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Compare properties side-by-side</h1>
                            <p className="text-white/60 text-sm mt-2">
                                {items.length}/{MAX_COMPARE} added · differences highlighted below
                            </p>
                        </div>
                        {items.length > 0 && (
                            <button
                                onClick={clear}
                                data-testid="compare-clear-all"
                                className="px-4 py-2 rounded-md border border-navy-500/30 text-navy-500 text-sm font-semibold hover:border-red-500/60 hover:text-red-600"
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[1,2,3,4].map((i) => <div key={i} className="h-64 rounded-2xl shimmer" />)}
                    </div>
                ) : empty ? (
                    <div className="text-center py-24 rounded-2xl border border-navy-500/10 bg-navy-950">
                        <Scale className="w-12 h-12 text-gold-500/60 mx-auto mb-4" />
                        <h3 className="font-heading text-2xl font-bold text-navy-500 mb-2">No properties to compare yet</h3>
                        <p className="text-navy-500/60 text-sm mb-6 max-w-md mx-auto">
                            Add up to 4 properties from the listing or details page to see a detailed side-by-side breakdown.
                        </p>
                        <Link
                            to="/properties"
                            className="text-on-image inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-gold-500 text-white font-semibold text-sm hover:bg-gold-600"
                            data-testid="compare-browse-cta"
                        >
                            Browse properties
                        </Link>
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0" data-testid="compare-matrix">
                        <div className="min-w-[720px]">
                            {/* Header row — property cards */}
                            <div className={`grid gap-4 mb-4`} style={{ gridTemplateColumns: `160px repeat(${items.length}, minmax(220px, 1fr))` }}>
                                <div />
                                {items.map((p) => (
                                    <div key={p.id} className="relative rounded-2xl overflow-hidden border border-navy-500/10 bg-white shadow-sm" data-testid={`compare-card-${p.id}`}>
                                        <button
                                            onClick={() => remove(p.id)}
                                            className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/95 border border-navy-500/10 flex items-center justify-center shadow-sm hover:bg-red-50 hover:border-red-300"
                                            data-testid={`compare-remove-${p.id}`}
                                            aria-label="Remove from comparison"
                                        >
                                            <X className="w-4 h-4 text-navy-500" />
                                        </button>
                                        <div className="aspect-[16/11] bg-navy-500/5">
                                            {p.images?.[0] ? (
                                                <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-navy-500/40">
                                                    <MapPin className="w-8 h-8" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3 space-y-1.5">
                                            <Link
                                                to={`/properties/${p.id}`}
                                                className="font-heading font-bold text-navy-500 text-sm line-clamp-2 hover:text-gold-600"
                                            >
                                                {p.title}
                                            </Link>
                                            <div className="text-gold-600 font-heading font-bold text-lg">
                                                {formatINR(p.price)}
                                                {p.listing_type === "rent" && <span className="text-xs font-medium text-navy-500/60 ml-1">/mo</span>}
                                            </div>
                                            <div className="text-[11px] text-navy-500/65 flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-gold-600" />
                                                <span className="line-clamp-1">{[p.locality, p.city].filter(Boolean).join(", ")}</span>
                                            </div>
                                            {(p.bhk != null || p.bathrooms != null || p.area_sqft != null) && (
                                                <div className="flex items-center gap-2.5 pt-1.5 border-t border-navy-500/5 text-[11px] text-navy-500/75">
                                                    {p.bhk != null && <span className="inline-flex items-center gap-1"><BedDouble className="w-3 h-3 text-gold-600" />{p.bhk}</span>}
                                                    {p.bathrooms != null && <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3 text-gold-600" />{p.bathrooms}</span>}
                                                    {p.area_sqft != null && <span className="inline-flex items-center gap-1"><Maximize className="w-3 h-3 text-gold-600" />{p.area_sqft}sf</span>}
                                                </div>
                                            )}
                                            {p.verified && (
                                                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                                    <ShieldCheck className="w-3 h-3" /> Verified
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Attribute rows */}
                            <div className="rounded-2xl border border-navy-500/10 overflow-hidden">
                                {visibleRows.map((row, idx) => {
                                    const values = items.map((p) => row.render(p));
                                    // Simple diff highlight — mark row as "differs" if unique stringified values > 1
                                    const uniq = new Set(values.map((v) => String(v)));
                                    const differs = uniq.size > 1 && items.length > 1;
                                    return (
                                        <div
                                            key={row.key}
                                            className={`grid gap-4 px-4 py-3 text-sm ${idx % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}`}
                                            style={{ gridTemplateColumns: `160px repeat(${items.length}, minmax(220px, 1fr))` }}
                                        >
                                            <div className={`font-semibold ${differs ? "text-gold-700" : "text-navy-500/80"} flex items-center gap-1.5`}>
                                                {row.label}
                                                {differs && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold-500/15 text-gold-700 font-bold">Differs</span>}
                                            </div>
                                            {values.map((v, i) => (
                                                <div key={i} className="text-navy-500 break-words">{v}</div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
